import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useApp } from "../vault/ctx";
import Ic from "../vault/Ic";
import { Pills, ToggleRow, Switch, Modal } from "../vault/Common";
import { createBatch, listBatches, listJobs, listAgents, type RenderJob, type Agent } from "../../features/render/renderApi";
import { listModels, type VaultModel } from "../../features/vault/vaultApi";
import QueueModal from "./QueueModal";
import BrowserRenderRunner from "./BrowserRenderRunner";

/** Resolution pill label → WxH (mirrors BulkModal's RESOLUTIONS). */
const RESOLUTIONS: Record<string, string> = { "2K": "2560x1440", "4K": "3840x2160", "8K": "7680x4320" };
/** Angle/metal pill labels → representative counts (for the estimate). */
// Must match BrowserRenderRunner.ANGLE_PRESETS, which offers exactly six — the
// number of CameraPresets the contract defines. The label used to read "8 full"
// and count 8, so picking it promised 8 images per model and delivered 6.
const ANGLE_COUNT: Record<string, number> = { "4 standard": 4, "6 full": 6, "Hero only": 1 };
const METAL_COUNT: Record<string, number> = { Inherit: 1, "All 4": 4, "As-is": 1 };

const TERMINAL = new Set(["done", "canceled", "error"]);

/** format string → [pill label, mini-ico bg color] (matches the reference palette). */
function fmtBadge(fmt?: string): [string, string] {
  const f = (fmt ?? "").toUpperCase();
  const color = f.includes("GLB") || f.includes("GLTF") ? "#33608F"
    : f.includes("STL") ? "#5A6B3B"
    : f.includes("STEP") || f.includes("STP") ? "#6B4B7A"
    : "#7A5C2E";
  return [f.replace(/[^A-Z0-9]/g, "").slice(0, 4) || "3D", color];
}

function jobStatusText(j: RenderJob): string {
  const w = Math.round(j.progress || 0);
  switch (j.status) {
    case "done": return "Done";
    case "error": return "Error";
    case "canceled": return "Canceled";
    case "queued": return "Queued";
    default: return w > 0 ? `Rendering · ${w}%` : "Rendering";
  }
}

export default function BulkRenderPage() {
  const { open, selected } = useApp();

  // Real data, loaded on mount.
  const [allModels, setAllModels] = useState<VaultModel[] | null>(null); // null = loading
  const [agents, setAgents] = useState<Agent[] | null>(null);

  // Batch + settings.
  const [files, setFiles] = useState<VaultModel[]>([]);
  const [compute, setCompute] = useState("cloud");
  const [useEffective, setUseEffective] = useState(true);
  // Canvas's "Render 4K" button hands its OUTPUT size over in router state, so
  // the label the user clicked is the size that actually renders.
  const handedRes = (useLocation().state as { resolution?: string } | null)?.resolution;
  const [res, setRes] = useState(handedRes && RESOLUTIONS[handedRes] ? handedRes : "4K");
  const [dpi, setDpi] = useState(300);
  const [anglesLabel, setAnglesLabel] = useState("4 standard");
  const [metalsLabel, setMetalsLabel] = useState("Inherit");
  const [parallelJobs, setParallelJobs] = useState(2);
  const [useAgents, setUseAgents] = useState<Set<string>>(new Set());

  // Run + queue.
  const [batchId, setBatchId] = useState<string | null>(null);
  const [jobs, setJobs] = useState<RenderJob[]>([]);
  const [starting, setStarting] = useState(false);
  const [startErr, setStartErr] = useState<string | null>(null);
  const [showQueue, setShowQueue] = useState(false);
  // Cloud batches render in THIS browser (the server worker is reaper-only — it no
  // longer fakes progress). Captured at start so mid-run UI tweaks don't affect it.
  const [runParams, setRunParams] = useState<{ angles: number; metals: number; width: number; height: number; video: boolean; useEffective: boolean } | null>(null);
  const [videoOut, setVideoOut] = useState(true); // also record a turntable video per model

  // Load real Drive models + render machines once.
  useEffect(() => {
    let alive = true;
    listModels(null, {})
      .then((ms) => {
        if (!alive) return;
        const ready = ms.filter((m) => m.status === "ready");
        setAllModels(ready);
        // Best-effort seed from the current Drive selection (matched by name or id).
        const seed = ready.filter((m) => selected.includes(m.name) || selected.includes(m._id));
        if (seed.length) setFiles(seed);
      })
      .catch(() => alive && setAllModels([]));
    listAgents()
      .then((a) => {
        if (!alive) return;
        setAgents(a);
        setUseAgents(new Set(a.filter((x) => x.status === "online").map((x) => x._id)));
      })
      .catch(() => alive && setAgents([]));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed from selection at mount only
  }, []);

  /*
   * Adopt an unfinished cloud batch that was queued somewhere else.
   *
   * Cloud batches render in THIS browser via <BrowserRenderRunner>, which only
   * mounts when `batchId` AND `runParams` are set — and both used to be set
   * exclusively inside start(). So the Drive path (select files → "Queue bulk
   * render", VaultModals) created a real batch with real jobs that NOTHING ever
   * rendered: it sat at "queued 0/N" forever, because the server worker is a
   * reaper only. Same for a batch left behind by a reload mid-run.
   *
   * The two creators write `options` differently — the Drive modal sends
   * angles/metals as string[], this page sends the pill labels — so accept both.
   */
  useEffect(() => {
    if (batchId) return;
    let alive = true;
    listBatches()
      .then((batches) => {
        if (!alive) return;
        const live = batches
          .filter((b) => b.compute === "cloud" && !TERMINAL.has(b.status) && b.doneJobs < b.totalJobs)
          .sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt))[0];
        if (!live) return;

        const opts = live.options ?? {};
        const countOf = (v: unknown, table: Record<string, number>) =>
          Array.isArray(v) ? Math.max(1, v.length) : table[String(v)] ?? 1;
        const [w, h] = (RESOLUTIONS[String(live.resolution)] ?? live.resolution ?? "3840x2160")
          .split("x")
          .map(Number);

        setBatchId(live._id);
        setRunParams({
          angles: countOf(opts.angles, ANGLE_COUNT),
          metals: countOf(opts.metals, METAL_COUNT),
          width: Number.isFinite(w) ? w : 3840,
          height: Number.isFinite(h) ? h : 2160,
          video: opts.video === true,
          // default ON, matching the toggle's own default
          useEffective: opts.useEffective !== false,
        });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [batchId]);

  // Poll the live batch to drive the "This batch" rows.
  useEffect(() => {
    if (!batchId) return;
    let alive = true;
    let t: ReturnType<typeof setInterval>;
    const load = () =>
      listJobs(batchId)
        .then((js) => {
          if (!alive) return;
          setJobs(js);
          if (js.length && js.every((j) => TERMINAL.has(j.status))) clearInterval(t);
        })
        .catch(() => {});
    load();
    t = setInterval(load, 3000);
    return () => { alive = false; clearInterval(t); };
  }, [batchId]);

  const onlineAgents = (agents ?? []).filter((a) => a.status === "online");
  const hasOnline = onlineAgents.length > 0;
  const primary = onlineAgents[0] ?? agents?.[0] ?? null;
  const additional = (agents ?? []).filter((a) => a._id !== primary?._id);

  const fileById = useMemo(() => new Map(files.map((f) => [f._id, f])), [files]);

  const n = files.length;
  const angleCount = ANGLE_COUNT[anglesLabel];
  const metalCount = METAL_COUNT[metalsLabel];
  const imgs = n * angleCount * metalCount;
  const mins = Math.max(2, Math.round((imgs * (compute === "cloud" ? 9.5 : 14)) / 60));
  const startDisabled = n === 0 || starting || (compute === "onprem" && !hasOnline);

  const toggleFile = (m: VaultModel) =>
    setFiles((fs) => (fs.some((f) => f._id === m._id) ? fs.filter((f) => f._id !== m._id) : [...fs, m]));

  const addSelected = () => {
    if (!allModels) return;
    const toAdd = allModels.filter((m) => selected.includes(m.name) || selected.includes(m._id));
    setFiles((fs) => {
      const have = new Set(fs.map((f) => f._id));
      return [...fs, ...toAdd.filter((m) => !have.has(m._id))];
    });
  };

  const start = async () => {
    if (startDisabled) return;
    setStarting(true);
    setStartErr(null);
    try {
      const { batch, jobs: created } = await createBatch({
        fileIds: files.map((f) => f._id),
        compute: compute as "cloud" | "onprem",
        resolution: RESOLUTIONS[res],
        dpi,
        options: {
          useEffective,
          angles: anglesLabel,
          metals: metalsLabel,
          ...(compute === "onprem" ? { parallelJobs, agents: [...useAgents] } : {}),
        },
      });
      setJobs(created);
      setBatchId(batch._id);
      // Cloud → render here in the browser; on-prem → real agents claim the jobs.
      if (compute === "cloud") {
        const [w, h] = (RESOLUTIONS[res] ?? "3840x2160").split("x").map(Number);
        setRunParams({ angles: angleCount, metals: metalCount, width: w, height: h, video: videoOut, useEffective });
      } else {
        setRunParams(null);
      }
    } catch (e) {
      setStartErr(e instanceof Error ? e.message : "Couldn't start bulk render");
    } finally {
      setStarting(false);
    }
  };

  return (
    <>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 280 }}>
          <div className="page-title">Bulk Render</div>
          <div className="page-sub">Drop in a batch of models — every file renders automatically with its own effective settings, on our cloud or your own machines. Works standalone, or send files here from any Drive selection.</div>
        </div>
        <button className="btn ghost" onClick={() => setShowQueue(true)}><Ic n="monitor" size={14} /> Render queue</button>
      </div>

      <div className="two-col" style={{ marginTop: 18 }}>
        <div>
          {/* 1 · files */}
          <div className="card">
            <div className="br-step"><span className="gnum">1</span><b>Add files</b><span className="hint">· {n} in batch{selected.length ? ` · ${selected.length} selected in Drive` : ""}</span></div>
            <div className="br-drop" onClick={() => open("upload")}>
              <div style={{ color: "var(--accent-strong)", opacity: .8, marginBottom: 6 }}><Ic n="upload" size={24} sw={1.5} /></div>
              <b style={{ fontSize: 13 }}>Drop models here</b>
              <div className="hint" style={{ marginTop: 3 }}>.3dm · .glb · .stl · .step · whole folders — or</div>
              <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 10 }}>
                <button className="btn ghost" style={{ fontSize: 12 }} onClick={e => { e.stopPropagation(); open("brDrivePicker"); }}>Choose from Drive</button>
                {selected.length > 0 && <button className="btn gold" style={{ fontSize: 12 }} onClick={e => { e.stopPropagation(); addSelected(); }}>Add {selected.length} selected</button>}
              </div>
            </div>
            <div style={{ marginTop: 10 }}>
              {files.map((f) => {
                const [ic, c] = fmtBadge(f.format);
                return (
                  <div key={f._id} className="br-file">
                    <span className="mini-ico" style={{ background: c }}>{ic}</span>{f.name}
                    <button className="bf-x" title="Remove" onClick={() => setFiles(fs => fs.filter((x) => x._id !== f._id))}>✕</button>
                  </div>
                );
              })}
              {n === 0 && <div className="hint" style={{ marginTop: 6 }}>No files yet — drop models above or choose from Drive.</div>}
            </div>
          </div>

          {/* 2 · settings */}
          <div className="card">
            <div className="br-step"><span className="gnum">2</span><b>Render settings</b></div>
            <ToggleRow title="Use each file's effective settings" sub="Model → subfolder → folder → global. The safest default." right={<Switch on={useEffective} onChange={setUseEffective} />} />
            <div className="frow" style={{ marginTop: 8 }}>
              <div className="field"><label>Resolution</label>
                <div className="pill-opts">{["2K", "4K", "8K"].map(r => <button key={r} className={res === r ? "on" : ""} onClick={() => setRes(r)}>{r}</button>)}</div></div>
              <div className="field"><label>DPI</label>
                <div className="pill-opts">{[72, 150, 300, 600].map(d => <button key={d} className={dpi === d ? "on" : ""} onClick={() => setDpi(d)}>{d}</button>)}</div></div>
            </div>
            <div className="frow">
              <div className="field"><label>Angles per model</label><Pills options={["4 standard", "6 full", "Hero only"]} value={anglesLabel} onChange={v => setAnglesLabel(v as string)} /></div>
              <div className="field"><label>Metal variants</label><Pills options={["Inherit", "All 4", "As-is"]} value={metalsLabel} onChange={v => setMetalsLabel(v as string)} /></div>
            </div>
            <ToggleRow
              title="Turntable video"
              sub="Also record a spinning 360° clip per model, alongside the stills."
              right={<Switch on={videoOut} onChange={setVideoOut} />}
            />
          </div>
        </div>

        <div>
          {/* 3 · compute */}
          <div className="card">
            <div className="br-step"><span className="gnum">3</span><b>Where to render</b></div>
            <div className="comp-grid">
              <div className={"comp-card" + (compute === "cloud" ? " on" : "")} onClick={() => setCompute("cloud")}>
                <span className="radio"></span>
                <b><Ic n="db" size={15} /> Vault Cloud</b>
                <div className="cc-meta">Priority GPU farm · no setup · finishes even if you close the tab. Outputs sync to your S3 automatically.<br /><b style={{ color: "var(--accent-strong)" }}>~9s / image · included in Studio plan</b></div>
              </div>
              <div className={"comp-card" + (compute === "onprem" ? " on" : "")} onClick={() => setCompute("onprem")}>
                <span className="radio"></span>
                <b><Ic n="monitor" size={15} /> On-premises</b>
                <div className="cc-meta">Runs on your own machines with the Vault Agent. Files never leave your network.<br />
                  {agents === null ? (
                    <span className="hint">Checking for machines…</span>
                  ) : primary ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 3 }}>
                      {primary.status === "online" && <span className="conn-dot"></span>}
                      <b style={primary.status === "online" ? { color: "var(--green)" } : undefined}>{primary.name}</b> · {primary.gpu ?? "GPU"} · {primary.status === "online" ? "online" : "offline"}
                    </span>
                  ) : (
                    <span style={{ marginTop: 3, display: "inline-block" }}>No render machines connected — install the Vault Agent.</span>
                  )}
                </div>
              </div>
            </div>
            {compute === "onprem" && (
              <div style={{ marginTop: 10 }}>
                {additional.map((a) => (
                  <ToggleRow
                    key={a._id}
                    title={`Also use ${a.name}`}
                    sub={`${a.gpu ?? "GPU"} · ${a.status === "online" ? "online" : "offline · wake-on-LAN"}`}
                    right={<Switch on={useAgents.has(a._id)} onChange={(v) => setUseAgents((s) => { const nx = new Set(s); if (v) nx.add(a._id); else nx.delete(a._id); return nx; })} />}
                  />
                ))}
                <div className="cs-slider"><label>Parallel jobs per machine<span className="code">{parallelJobs}</span></label><input type="range" min="1" max="8" value={parallelJobs} onChange={(e) => setParallelJobs(+e.target.value)} /></div>
                <div className="anno" style={{ marginTop: 8, fontSize: 11 }}>Install the Vault Agent on more machines to grow your farm — Settings → Storage/S3 → Agents.</div>
              </div>
            )}
          </div>

          {/* 4 · start */}
          <div className="card">
            <div className="br-step"><span className="gnum">4</span><b>Start</b></div>
            <div className="bulk-est" style={{ background: "var(--accent-soft)", border: "1px solid rgba(255,255,255,.55)", borderRadius: 14, padding: "12px 15px", fontSize: 12.5 }}>
              ≈ <b>{imgs.toLocaleString()} images</b> ({n} files × {angleCount} angles × {metalCount} metals) · <b>{res} · {dpi} DPI</b> · ~<b>{mins} min</b> on {compute === "cloud" ? "Vault Cloud" : "your workstation"}
            </div>
            <button className="btn gold" style={{ width: "100%", justifyContent: "center", marginTop: 12 }} disabled={startDisabled} onClick={start}>
              <Ic n="bolt" size={14} /> Start bulk render ({n} files)
            </button>
            {compute === "onprem" && agents !== null && !hasOnline && <div className="hint" style={{ marginTop: 8, textAlign: "center" }}>No online machine — bring a Vault Agent online to render on-premises.</div>}
            {startErr && <div className="hint" style={{ marginTop: 8, textAlign: "center", color: "#B4342E" }}>{startErr}</div>}
            <div className="hint" style={{ marginTop: 8, textAlign: "center" }}>Outputs save next to each model · clients on live links see new renders automatically.</div>
          </div>

          {jobs.length > 0 && (
            <div className="card">
              <h3>This batch</h3>
              {jobs.map((j, i) => {
                const m = fileById.get(j.fileId);
                const [ic, c] = fmtBadge(m?.format);
                const w = j.status === "done" ? 100 : Math.round(j.progress || 0);
                return (
                  <div key={j._id} className="qrow">
                    <span className="mini-ico" style={{ background: c }}>{ic}</span>
                    <div style={{ width: 170 }}><b style={{ fontSize: 12.5 }}>{m?.name ?? "Model"}</b>
                      <div className="hint">{jobStatusText(j)}</div></div>
                    <div className="prog"><i style={{ width: w + "%" }}></i></div>
                    {i === 0 && <span className="badge gold">{compute === "cloud" ? "cloud" : "on-prem"}</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Real Drive picker — lists root models via listModels(null,{}). */}
      <Modal id="brDrivePicker" maxWidth={520} title="Choose from Drive">
        {allModels === null ? (
          <div className="hint" style={{ padding: "12px 2px" }}>Loading models…</div>
        ) : allModels.length === 0 ? (
          <div className="hint" style={{ padding: "12px 2px" }}>No models in this workspace yet.</div>
        ) : (
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {allModels.map((m) => {
              const [ic, c] = fmtBadge(m.format);
              const inBatch = files.some((f) => f._id === m._id);
              return (
                <div key={m._id} className="br-file" style={{ cursor: "pointer" }} onClick={() => toggleFile(m)}>
                  <span className="mini-ico" style={{ background: c }}>{ic}</span>{m.name}
                  <button className="bf-x" title={inBatch ? "Remove from batch" : "Add to batch"} onClick={(e) => { e.stopPropagation(); toggleFile(m); }}>{inBatch ? "✓" : "+"}</button>
                </div>
              );
            })}
          </div>
        )}
      </Modal>

      {showQueue && <QueueModal onClose={() => setShowQueue(false)} />}

      {/* Cloud batches render in a hidden offscreen viewer in THIS browser (real
          frames via the 3D engine), reporting outputs/progress to the API. Mounted
          only while the batch has pending jobs; unmounts (disposes the viewer) when
          every job reaches a terminal state. */}
      {runParams && batchId && jobs.some((j) => !TERMINAL.has(j.status)) && (
        <BrowserRenderRunner
          jobs={jobs.filter((j) => !TERMINAL.has(j.status)).map((j) => ({ _id: j._id, fileId: j.fileId }))}
          angles={runParams.angles}
          metals={runParams.metals}
          width={runParams.width}
          height={runParams.height}
          video={runParams.video}
          useEffective={runParams.useEffective}
          onProgress={() => { if (batchId) listJobs(batchId).then(setJobs).catch(() => {}); }}
        />
      )}
    </>
  );
}
